import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { createFormControl, DestroyableBase, TypedFormControl } from 'osn-shared';
import { ReplaySubject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { deepEqual } from '../../functions/deepEqual';
import { AlwaysError } from '../../../core/services/always-error.service';

interface TypeWithTitle {
  title: string;
}

@Component({
  selector: 'osn-cxb-single-select',
  templateUrl: './single-select.component.html',
  styleUrls: ['./single-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleSelectComponent),
      multi: true,
    },
  ],
})
export class SingleSelectComponent<T extends TypeWithTitle>
  extends DestroyableBase
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  @ViewChild('singleSelect', { static: true }) public singleSelect: MatSelect;
  @Input() public typeFirstVersion = false;
  @Input() public disableMultiSelect: boolean;
  @Input() public options: T[];
  @Input() public placeholder: string;
  @Input() public filterNoEntriesFoundLabel = 'No entries found';
  @Input() public filterPlaceholderLabel = 'Filter entries...';
  @Input() public hintLabel: string;
  @Input() public errorLabel: string;
  @Input() public label: string;
  @Input() public emitValueOnRegisterOnChange = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('data-testid') public testId = 'single-select-component';

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};

  public selectCtrl: TypedFormControl<T>;
  public filterCtrl: TypedFormControl<string> = createFormControl('');
  public filteredOptions$: ReplaySubject<T[]> = new ReplaySubject<T[]>(1);

  constructor(public readonly alwaysError: AlwaysError) {
    super();
  }

  public ngOnInit() {
    this.selectCtrl = createFormControl<T>(this.options[0]);
    this.filteredOptions$.next(this.typeFirstVersion ? [] : this.options?.slice() || []);
    this.selectCtrl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged((a, b) => deepEqual(a, b))
      )
      .subscribe((selectedOptions: T) => {
        this.propagateChange?.(selectedOptions);
      });

    this.filterCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.filterMultiOptions();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.options?.currentValue?.length) {
      return;
    }
    this.filteredOptions$.next(this.typeFirstVersion ? [] : changes.options.currentValue);
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public writeValue(value: T): void {
    this.selectCtrl.setValue(value);
    this.filterMultiOptions();
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;

    if (this.emitValueOnRegisterOnChange) {
      this.propagateChange?.(this.selectCtrl.value as T);
    }
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectCtrl.disable();
    } else {
      this.selectCtrl.enable();
    }
  }

  public compareFunction(option1: T, option2: T): boolean {
    return option1 === option2;
  }

  private setInitialValue() {
    this.filteredOptions$.pipe(take(1), takeUntil(this.onDestroy$)).subscribe(() => {
      this.singleSelect.compareWith = (a: T, b: T) => a && b && a.title === b.title;
    });
  }

  private filterMultiOptions() {
    if (!this.options) {
      return;
    }
    let search = this.filterCtrl.value;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (!search || (this.typeFirstVersion && search.length < 3)) {
      this.filteredOptions$.next(this.typeFirstVersion ? [this.selectCtrl.value] : this.options?.slice() || []);
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredOptions$.next(
      this.options.filter((option) => {
        return option.title.toLowerCase().indexOf(search) > -1;
      })
    );
  }
}
